import { env } from "@/shared/config/env";
import { openUpgradePrompt } from "@/shared/lib/upgradePrompt";
import { getAuthToken, notifyAuthError } from "./client";
import { ApiError, type ApiErrorBody } from "./errors";

/**
 * Hand-written Server-Sent Events client for the chat run stream. The generated
 * `openapi-fetch` client can't model a streaming `text/event-stream` body, so
 * chat streaming lives here — the one sanctioned exception to "nothing else
 * fetches" (it still flows auth + the 402 upgrade guard, mirroring `client.ts`).
 *
 * Wire format (see backend `internal/sse`): each event is a block separated by a
 * blank line, e.g. `event: delta\ndata: {"content":"hi"}\n\n`. The stream ends
 * with a bare `data: [DONE]` sentinel (no `event:` line).
 */
export interface SseEvent {
  /** The `event:` field; defaults to `"message"` per the SSE spec when absent. */
  event: string;
  /** The concatenated `data:` payload (raw string; usually JSON). */
  data: string;
}

/** The sentinel the backend writes to mark the end of a stream. */
export const SSE_DONE = "[DONE]";

/**
 * Pure incremental parser: given the current text buffer, return every COMPLETE
 * event block parsed out of it plus the unparsed remainder (a partial trailing
 * block held until more bytes arrive). Split out from the network so it can be
 * unit-tested against arbitrary chunk boundaries.
 */
export function decodeSseBuffer(buffer: string): {
  events: SseEvent[];
  rest: string;
} {
  const normalized = buffer.replace(/\r\n/g, "\n");
  const events: SseEvent[] = [];
  let rest = normalized;

  let boundary = rest.indexOf("\n\n");
  while (boundary !== -1) {
    const block = rest.slice(0, boundary);
    rest = rest.slice(boundary + 2);
    const parsed = parseSseBlock(block);
    if (parsed) events.push(parsed);
    boundary = rest.indexOf("\n\n");
  }

  return { events, rest };
}

/** Parse one block ("event:"/"data:" lines). Returns null for empty/comment blocks. */
function parseSseBlock(block: string): SseEvent | null {
  let event = "message";
  const dataLines: string[] = [];

  for (const rawLine of block.split("\n")) {
    const line = rawLine.trimEnd();
    if (line === "" || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    // The SSE spec strips a single leading space after the colon.
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);

    if (field === "event") event = value;
    else if (field === "data") dataLines.push(value);
  }

  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join("\n") };
}

export interface StreamSseOptions {
  /** Path under the API base URL, e.g. `"/v1/chat/runs"`. */
  path: string;
  /** JSON request body. */
  body: unknown;
  /** Abort the underlying fetch (hard stop, e.g. on unmount). */
  signal?: AbortSignal;
  /** Called once per parsed event, in arrival order, until the `[DONE]` sentinel. */
  onEvent: (event: SseEvent) => void;
}

/**
 * POST a JSON body and stream the SSE response, invoking `onEvent` for each
 * event until the `[DONE]` sentinel or the body closes. Non-2xx responses are
 * normalized to `ApiError` (and a 402 `quota_exceeded` opens the upgrade
 * prompt, exactly like the shared client's quota middleware). Resolves when the
 * stream ends; rejects on transport/HTTP error (an `AbortError` if cancelled).
 */
export async function streamSse({ path, body, signal, onEvent }: StreamSseOptions): Promise<void> {
  const send = (token: string | null) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${env.API_BASE_URL}${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal,
    });
  };

  let response = await send(await getAuthToken());

  // A rejected token (e.g. it lapsed) — force a fresh one and retry once. The
  // request was refused at the auth gate before any run was created, so the
  // retry can't double-send.
  if (response.status === 401) {
    const refreshed = await getAuthToken({ skipCache: true }).catch(() => null);
    if (refreshed) response = await send(refreshed);
  }

  if (!response.ok || !response.body) {
    // Still unauthorized after a refresh → the session is gone; recover (sign
    // out → redirect to sign-in) via the registered handler.
    if (response.status === 401) notifyAuthError();
    const payload = (await response
      .json()
      .catch(() => null)) as { error?: ApiErrorBody } | null;
    if (response.status === 402 && payload?.error?.code === "quota_exceeded") {
      openUpgradePrompt();
    }
    throw new ApiError(response.status, payload?.error);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { events, rest } = decodeSseBuffer(buffer);
      buffer = rest;
      for (const event of events) {
        if (event.data === SSE_DONE) return;
        onEvent(event);
      }
    }
  } finally {
    reader.cancel().catch(() => undefined);
  }
}
